import { Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Copy, X } from 'lucide-react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Badge, Button } from 'slab'
import {
  useGetBrokersMembersMemberIdStripeIssuingCardsCardIdCardholderDetails,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdEphemeralKey,
} from 'utils/web_api'
import { type BrokersStripeIssuingCardCardholderDetails } from 'utils/web_api.schemas'

import BrokersMemberContext from '../context/BrokersMemberContext'
import CopyButtonWithAnimation from '../reusable/CopyButtonWithAnimation'

// Custom hook to fetch ephemeral key data once.
// This is used to fetch data that's required to fetch card details from Stripe. There's a lot of
// logic in here that attempts to prevent the same data from being fetched multiple times. We don't want
// to fetch data from Stripe a bunch and cause Stripe to throttle us.
const useEphemeralKeyData = (
  memberId: string,
  cardId: string,
  cardDetails: BrokersStripeIssuingCardCardholderDetails | undefined
) => {
  const stripe = useStripe()
  const [data, setData] = useState<{
    nonce: string | null
    ephemeralKeySecret: string | null
    isLoading: boolean
    error: string | null
  }>({
    nonce: null,
    ephemeralKeySecret: null,
    isLoading: false,
    error: null,
  })

  const hasFetched = useRef(false)

  const ephemeralKeyMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdEphemeralKey({
      mutation: {
        retry: false,
      },
    })

  useEffect(() => {
    const fetchData = async () => {
      if (!stripe || !cardDetails?.remote_id || hasFetched.current) return

      hasFetched.current = true
      setData((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        const nonceResult = await stripe.createEphemeralKeyNonce({
          issuingCard: cardDetails.remote_id,
        })

        if (!nonceResult || !nonceResult.nonce) {
          throw new Error('Failed to create ephemeral key nonce')
        }

        const ephemeralKeyResult = await ephemeralKeyMutation.mutateAsync({
          memberId,
          cardId,
          data: { nonce: nonceResult.nonce },
        })

        const ephemeralKeySecret = (
          ephemeralKeyResult as { ephemeralKeySecret: string }
        ).ephemeralKeySecret

        setData({
          nonce: nonceResult.nonce,
          ephemeralKeySecret,
          isLoading: false,
          error: null,
        })
      } catch (error) {
        console.error('Failed to fetch ephemeral key data:', error)
        setData({
          nonce: null,
          ephemeralKeySecret: null,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    }

    if (cardDetails && stripe) {
      fetchData()
    }
  }, [cardDetails, stripe, memberId, cardId, ephemeralKeyMutation])

  return data
}

// Element configuration
const ELEMENT_CONFIG = {
  style: {
    base: {
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
      fontSize: '14px',
    },
  },
}

// Custom hook for managing issuing elements
const useIssuingElements = (
  cardDetails: BrokersStripeIssuingCardCardholderDetails | undefined,
  nonce: string | null,
  ephemeralKeySecret: string | null
) => {
  const stripe = useStripe()
  const elements = useElements()

  // Refs for element containers
  const elementRefs = {
    cardNumber: useRef<HTMLDivElement>(null),
    cardCvc: useRef<HTMLDivElement>(null),
    cardExpiry: useRef<HTMLDivElement>(null),
  }

  // State
  const [state, setState] = useState({
    isReady: false,
    isLoading: false,
    hasError: false,
  })

  // Store mounted elements for cleanup
  const mountedElementsRef = useRef<Record<string, any>>({})

  // Element definitions
  const elementDefinitions = [
    {
      type: 'issuingCardNumberDisplay' as const,
      ref: elementRefs.cardNumber,
      key: 'cardNumber',
    },
    {
      type: 'issuingCardCvcDisplay' as const,
      ref: elementRefs.cardCvc,
      key: 'cardCvc',
    },
    {
      type: 'issuingCardExpiryDisplay' as const,
      ref: elementRefs.cardExpiry,
      key: 'cardExpiry',
    },
  ]

  const initializeElements = useCallback(() => {
    if (
      !stripe ||
      !elements ||
      !cardDetails?.remote_id ||
      !nonce ||
      !ephemeralKeySecret
    ) {
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, hasError: false }))

    try {
      // Create and mount all elements
      const elementConfig = {
        issuingCard: cardDetails.remote_id,
        nonce,
        ephemeralKeySecret,
        ...ELEMENT_CONFIG,
      }

      let readyCount = 0
      const totalElements = elementDefinitions.length

      elementDefinitions.forEach(({ type, ref, key }) => {
        if (ref.current) {
          const element = elements.create(type as any, elementConfig)
          element.mount(ref.current)
          mountedElementsRef.current[key] = element

          // Track ready state
          element.on('ready', () => {
            readyCount++
            if (readyCount === totalElements) {
              setState((prev) => ({ ...prev, isReady: true, isLoading: false }))
            }
          })
        }
      })
    } catch (error) {
      console.error('Failed to initialize Stripe elements:', error)
      setState((prev) => ({ ...prev, hasError: true, isLoading: false }))
    }
  }, [stripe, elements, cardDetails, nonce, ephemeralKeySecret])

  // Cleanup function
  const cleanup = useCallback(() => {
    Object.values(mountedElementsRef.current).forEach((element) => {
      if (element) {
        element.unmount()
      }
    })
    mountedElementsRef.current = {}
    setState({ isReady: false, isLoading: false, hasError: false })
  }, [])

  // Effect for initialization and cleanup
  useEffect(() => {
    if (cardDetails && stripe && elements && nonce && ephemeralKeySecret) {
      initializeElements()
    }
    return cleanup
  }, [
    cardDetails,
    stripe,
    elements,
    nonce,
    ephemeralKeySecret,
    initializeElements,
    cleanup,
  ])

  return {
    ...state,
    elementRefs,
    cleanup,
  }
}

const StripeCardDetailsSkeleton: React.FC = () => {
  return (
    <div className='animate-pulse'>
      {/* Card Display Skeleton */}
      <div className='bg-white border rounded-lg p-4 mb-6'>
        <div className='mb-4'>
          <div className='w-full h-8 bg-gray-200 rounded mb-4' />
        </div>

        <div className='grid grid-cols-5 gap-1'>
          <div className='col-span-4'>
            {/* Card Number Skeleton */}
            <div className='mb-2'>
              <div className='h-5 bg-gray-200 rounded w-48' />
            </div>

            <div className='grid grid-cols-2 gap-4 mb-2'>
              {/* CVV Skeleton */}
              <div className='flex items-center gap-2'>
                <div className='h-4 bg-gray-200 rounded w-8' />
                <div className='h-4 bg-gray-200 rounded w-8' />
              </div>

              {/* Expiry Skeleton */}
              <div className='flex items-center gap-2'>
                <div className='h-4 bg-gray-200 rounded w-8' />
                <div className='h-4 bg-gray-200 rounded w-12' />
              </div>
            </div>

            {/* Cardholder Name Skeleton */}
            <div className='mt-2'>
              <div className='h-4 bg-gray-200 rounded w-32' />
            </div>
          </div>

          <div className='col-span-1 text-right'>
            <div className='mt-1'>
              <div className='w-8 h-6 bg-gray-200 rounded' />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address Skeleton */}
      <div className='mb-6'>
        <div className='h-3 bg-gray-200 rounded w-24 mb-2' />
        <div className='space-y-2'>
          <div className='h-4 bg-gray-200 rounded w-full' />
          <div className='h-4 bg-gray-200 rounded w-3/4' />
          <div className='h-4 bg-gray-200 rounded w-2/3' />
          <div className='h-4 bg-gray-200 rounded w-1/2' />
          <div className='h-4 bg-gray-200 rounded w-1/3' />
        </div>
      </div>

      {/* Footer Message Skeleton */}
      <div className='h-4 bg-gray-200 rounded w-full' />
    </div>
  )
}

// Address field component for reusability
const AddressField: React.FC<{ value: string; label?: string }> = ({
  value,
}) => (
  <div className='flex items-center gap-2'>
    <span>{value}</span>
    <CopyButtonWithAnimation content={value}>
      <Copy size={16} className='text-gray-400' />
    </CopyButtonWithAnimation>
  </div>
)

interface StripeCardDetailsContentProps {
  memberId: string
  cardId: string
  enrollmentId: string
  onClose: () => void
}

const StripeCardDetailsContent: React.FC<StripeCardDetailsContentProps> = ({
  memberId,
  cardId,
  enrollmentId,
  onClose,
}) => {
  const { visaImagePath } = useContext(BrokersMemberContext)

  const {
    data: cardDetails,
    isLoading,
    isError,
  } = useGetBrokersMembersMemberIdStripeIssuingCardsCardIdCardholderDetails(
    memberId,
    cardId
  )

  // Fetch ephemeral key data once
  const ephemeralKeyData = useEphemeralKeyData(memberId, cardId, cardDetails)

  const {
    isReady: elementsReady,
    isLoading: elementsLoading,
    hasError: elementsError,
    elementRefs,
  } = useIssuingElements(
    cardDetails,
    ephemeralKeyData.nonce,
    ephemeralKeyData.ephemeralKeySecret
  )

  // Helper to render address fields
  const renderAddressFields = () => {
    const address = cardDetails?.cardholder_address
    if (!address) return null

    const fields = [
      { key: 'line1', value: address.line1 },
      { key: 'line2', value: address.line2 },
      { key: 'city', value: address.city },
      { key: 'state', value: address.state },
      { key: 'postal_code', value: address.postal_code },
    ]

    return fields
      .filter((field) => field.value && typeof field.value === 'string')
      .map((field) => (
        <AddressField key={field.key} value={String(field.value)} />
      ))
  }

  return (
    <div className='bg-white rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto'>
      {/* Header */}
      <div className='flex justify-between items-start p-8 border-b'>
        <div className='flex items-center gap-2'>
          <h2 className='text-xl font-semibold'>Payment Details</h2>
          <Badge type='info'>{enrollmentId.slice(-6)}</Badge>
        </div>
        <button
          onClick={onClose}
          className='text-gray-400 hover:text-gray-600 hover:cursor-pointer'
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className='p-8'>
        {isLoading && <StripeCardDetailsSkeleton />}

        {isError && (
          <div className='text-center py-8'>
            <p className='text-red-500'>
              Card details could not be loaded. Please try again later.
            </p>
          </div>
        )}

        {cardDetails && (
          <>
            {/* Card Display */}
            <div className='bg-white border rounded-lg p-4 mb-6 font-mono text-sm'>
              <div className='mb-4'>
                <div className='w-full h-8 bg-gray-700 mb-4' />
              </div>

              <div className='grid grid-cols-5 gap-1'>
                <div className='col-span-4'>
                  {/* Card Number */}
                  <div className='mb-2'>
                    {!elementsReady && (
                      <span>•••• •••• •••• {cardDetails.last4}</span>
                    )}
                    <div
                      ref={elementRefs.cardNumber}
                      className={elementsReady ? 'block ph-no-capture' : 'hidden'}
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4 mb-2'>
                    {/* CVV */}
                    <div className='flex items-center gap-2'>
                      <span>CVV</span>
                      {!elementsReady && <span>•••</span>}
                      <div
                        ref={elementRefs.cardCvc}
                        className={elementsReady ? 'block min-w-20 ph-no-capture' : 'hidden'}
                      />
                    </div>

                    {/* Expiry */}
                    <div className='flex items-center gap-2'>
                      <span>EXP</span>
                      {!elementsReady && <span>••/••</span>}
                      <div
                        ref={elementRefs.cardExpiry}
                        className={elementsReady ? 'block min-w-20 ph-no-capture' : 'hidden'}
                      />
                    </div>
                  </div>

                  <div className='mt-2 uppercase text-xs'>
                    {cardDetails.cardholder_name?.toUpperCase()}
                  </div>
                </div>

                {visaImagePath
                  ? (
                    <div className='col-span-1 text-right'>
                      <div className='mt-1'>
                        <img
                          src={visaImagePath}
                          alt='Visa'
                          className='w-12 h-auto'
                        />
                      </div>
                    </div>
                    )
                  : (
                    <div className='col-span-1 text-right'>
                      <Badge type='info'>Visa</Badge>
                    </div>
                    )}
              </div>
            </div>

            {/* Loading/Error states for Stripe elements */}
            {elementsLoading && (
              <div className='mb-4 text-center'>
                <p className='text-sm text-gray-600'>
                  Loading card details from Stripe...
                </p>
              </div>
            )}

            {elementsError && (
              <div className='mb-4 text-center'>
                <p className='text-sm text-red-500'>
                  Card details could not be loaded. Please try again later.
                </p>
              </div>
            )}

            {/* Billing Address */}
            <div className='mb-6'>
              <div className='text-xs text-gray-400 uppercase mb-2'>
                Billing address
              </div>
              <div className='text-sm space-y-2'>{renderAddressFields()}</div>
            </div>

            {/* Footer Message */}
            <p className='text-sm text-gray-600'>
              This card should only be used for {cardDetails.cardholder_name}'s
              enrollment premiums.
            </p>
          </>
        )}
      </div>

      {/* Footer */}
      <div className='p-4 border-t'>
        <div className='w-full'>
          <Button variant='secondary' onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

interface StripeCardDetailsModalProps {
  memberId: string
  cardId: string
  enrollmentId: string
  isOpen: boolean
  onClose: () => void
  stripePublishableKey: string
}

const StripeCardDetailsModal: React.FC<StripeCardDetailsModalProps> = ({
  memberId,
  cardId,
  enrollmentId,
  isOpen,
  onClose,
  stripePublishableKey,
}) => {
  // Memoize stripe promise to avoid recreating on every render
  const stripePromise = useMemo(() => {
    return stripePublishableKey ? loadStripe(stripePublishableKey) : null
  }, [stripePublishableKey])

  if (!isOpen || !stripePromise) return null

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <Elements stripe={stripePromise}>
        <StripeCardDetailsContent
          memberId={memberId}
          cardId={cardId}
          enrollmentId={enrollmentId}
          onClose={onClose}
        />
      </Elements>
    </div>
  )
}

export default StripeCardDetailsModal
